import React, { Children } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: 'easeOut',
      staggerChildren: 0.1
    }
  }
};
const childVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const Divider = () => <div className="w-full flex justify-center py-12" data-id="element-1">
    <div className="w-16 h-0.5 bg-[#00BFA6]/30 rounded-full" data-id="element-2"></div>
  </div>;
export function Presentation() {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  return <main className="w-full min-h-screen bg-[#FAFAFA] text-[#1a1a2e] font-sans selection:bg-[#00BFA6]/20 selection:text-[#00BFA6] relative overflow-hidden" data-id="element-3">
      {/* SCROLL PROGRESS BAR */}
      <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-[#00BFA6] origin-left z-50" style={{
      scaleX
    }} data-id="element-4" />

      {/* SECTION 1 — HERO */}
      <section className="min-h-screen flex flex-col items-center justify-center text-center px-8 relative bg-dot-grid" data-id="element-5">
        <motion.div initial="hidden" animate="visible" variants={sectionVariants} className="max-w-5xl mx-auto flex flex-col items-center relative z-10" data-id="element-6">
          <motion.h1 variants={childVariants} className="text-5xl md:text-8xl font-bold tracking-tight mb-8 text-slate-900 leading-tight" data-id="element-7">
            Designing Within{' '}
            <span className="relative inline-block text-[#00BFA6]" data-id="element-8">
              Constraints
              <motion.span initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.8,
              duration: 0.8,
              ease: 'easeOut'
            }} className="absolute -bottom-2 left-0 right-0 h-1.5 md:h-2 bg-[#00BFA6] origin-left rounded-full opacity-80" data-id="element-9" />
            </span>
          </motion.h1>
          <motion.p variants={childVariants} className="text-xl md:text-3xl text-slate-500 mb-14 max-w-3xl" data-id="element-10">
            FGE NexantECA Energy Platform, Infrastructure Map
          </motion.p>
          <motion.div variants={childVariants} className="h-1 w-24 bg-[#00BFA6] mb-14 rounded-full" data-id="element-11" />
          <motion.p variants={childVariants} className="text-xl text-slate-600 font-medium" data-id="element-12">
            Senior Interaction Designer, Made Tech
          </motion.p>
          <motion.p variants={childVariants} className="text-lg text-slate-400 mt-4 font-medium tracking-wide uppercase" data-id="element-13">
            1-month engagement
          </motion.p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-12 flex flex-col items-center gap-4 text-sm text-slate-400 font-medium tracking-widest uppercase" data-id="element-14">
          <span data-id="element-15">Scroll to explore</span>
          <motion.div animate={{
          y: [0, 6, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} className="w-5 h-5 border-b-2 border-r-2 border-[#00BFA6]/60 transform rotate-45 mt-[-4px]" data-id="element-16" />
        </motion.div>

        {/* Subtle fade out at bottom of hero */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAFAFA] to-transparent z-0 pointer-events-none" data-id="element-17"></div>
      </section>

      {/* SECTION 2 — CONTEXT */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 relative" data-id="element-18">
        <div className="relative mb-16" data-id="element-19">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-20">
            01
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-21">
            <span className="inline-block bg-emerald-50 text-[#00BFA6] px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm border border-emerald-100/50" data-id="element-22">
              CONTEXT
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-23">
              What we were building and why I was brought in
            </h2>
            <p className="text-xl text-slate-500 max-w-3xl leading-relaxed" data-id="element-24">
              Two legacy energy organisations were merging their data products
              into one platform for the first time. High stakes, high
              visibility, and a fixed launch date. They needed a designer who
              could hit the ground running.
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col md:flex-row gap-8 mb-16" data-id="element-25">
          <motion.div variants={childVariants} className="flex-1 bg-white border border-slate-200 border-l-[3px] border-l-[#00BFA6] shadow-md rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-26">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-27"></div>
            <span className="uppercase text-xs tracking-widest text-slate-400 font-semibold mb-5 block" data-id="element-28">
              THE PRODUCT
            </span>
            <h3 className="text-2xl font-bold text-slate-900 mb-5" data-id="element-29">
              Energy Intelligence Platform
            </h3>
            <p className="text-slate-600 leading-relaxed text-lg" data-id="element-30">
              A unified data product bringing together crude oil, refined
              products, NGL, gas and LNG market intelligence from FGE with
              NexantECA consulting expertise. It covers the entire energy value
              chain, including green energy.
            </p>
          </motion.div>
          <motion.div variants={childVariants} className="flex-1 bg-white border border-slate-200 border-l-[3px] border-l-[#00BFA6] shadow-md rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-31">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-32"></div>
            <span className="uppercase text-xs tracking-widest text-slate-400 font-semibold mb-5 block" data-id="element-33">
              MY FOCUS
            </span>
            <h3 className="text-2xl font-bold text-slate-900 mb-5" data-id="element-34">
              Infrastructure Map + General UX
            </h3>
            <p className="text-slate-600 leading-relaxed text-lg" data-id="element-35">
              An interactive infrastructure map where users can toggle data
              layers on and off and see live results. I joined at the final MVP
              stages to lead UX and product design, making sure we could ship a
              cohesive experience on time.
            </p>
          </motion.div>
        </div>

        <motion.div variants={childVariants} className="bg-amber-50 border border-amber-200/60 rounded-2xl p-10 shadow-sm" data-id="element-36">
          <p className="text-amber-800 text-xl text-center font-medium leading-relaxed" data-id="element-37">
            I was brought in because the team needed someone who could assess
            the current state quickly, make design decisions fast, and remove
            blockers so engineering could keep moving.
          </p>
        </motion.div>
      </motion.section>

      <Divider data-id="element-38" />

      {/* SECTION 3 — PROCESS */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 relative" data-id="element-39">
        <div className="relative mb-20" data-id="element-40">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-41">
            02
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-42">
            <span className="inline-block bg-emerald-50 text-[#00BFA6] px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm border border-emerald-100/50" data-id="element-43">
              PROCESS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight" data-id="element-44">
              How I approached it
            </h2>
          </motion.div>
        </div>

        <div className="relative mb-24" data-id="element-45">
          <div className="absolute top-7 left-14 right-14 h-1 bg-gradient-to-r from-slate-200 via-[#00BFA6]/40 to-slate-200 z-0 hidden md:block rounded-full" data-id="element-46"></div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative z-10" data-id="element-47">
            {[{
            num: '01',
            title: 'Discovery',
            desc: 'Stakeholder interviews, auditing what already existed, and benchmarking against other energy analytics platforms.'
          }, {
            num: '02',
            title: 'Define',
            desc: 'Mapping out user personas, key jobs to be done, and the information architecture for the unified data model.'
          }, {
            num: '03',
            title: 'Prototype',
            desc: 'Built map layer prototypes in Mapbox and MapTiler. Created interactive Figma prototypes to get stakeholder alignment.'
          }, {
            num: '04',
            title: 'Iterate',
            desc: 'Rapid design and dev cycles, adapting to shifting scope and tooling changes as they came up.'
          }, {
            num: '05',
            title: 'Deliver',
            desc: 'Handed engineering a launch-ready UX with full documentation and dev notes. Then wrote the fast-follow plan.'
          }].map((step, i) => <motion.div key={i} variants={childVariants} className="flex flex-col items-center md:items-start text-center md:text-left" data-id="element-48">
                <div className="w-14 h-14 rounded-full border-[3px] border-[#00BFA6] bg-emerald-50 text-[#00BFA6] flex items-center justify-center text-base font-bold mb-6 shadow-md mx-auto md:mx-0 relative" data-id="element-49">
                  {step.num}
                  <div className="absolute inset-0 rounded-full bg-white/50" data-id="element-50"></div>
                  <span className="relative z-10" data-id="element-51">{step.num}</span>
                </div>
                <h3 className="font-bold text-slate-900 text-xl mb-3 w-full" data-id="element-52">
                  {step.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed" data-id="element-53">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-54">
          <motion.div variants={childVariants} className="bg-white border border-slate-200 border-l-4 border-l-[#00BFA6] shadow-md rounded-r-2xl p-10 relative overflow-hidden" data-id="element-55">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-56"></div>
            <span className="uppercase text-xs tracking-widest text-slate-400 font-semibold mb-5 block" data-id="element-57">
              PROTOTYPING APPROACH
            </span>
            <p className="text-slate-600 leading-relaxed text-lg" data-id="element-58">
              Started with Mapbox for the initial layer interaction concepts,
              then evaluated MapTiler when licensing and performance constraints
              came up. Built clickable Figma prototypes to test the layer
              control UX before any engineering effort was committed.
            </p>
          </motion.div>
          <motion.div variants={childVariants} className="bg-white border border-slate-200 border-l-4 border-l-[#00BFA6] shadow-md rounded-r-2xl p-10 relative overflow-hidden" data-id="element-59">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-60"></div>
            <span className="uppercase text-xs tracking-widest text-slate-400 font-semibold mb-5 block" data-id="element-61">
              RESEARCH
            </span>
            <p className="text-slate-600 leading-relaxed text-lg" data-id="element-62">
              Spoke with analysts and traders to understand how they actually
              consumed market data. Reviewed the existing FGE and NexantECA
              tooling to find redundancy, gaps, and friction points that needed
              to be addressed in the new unified product.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* SECTION 4 — MAP SHOWCASE */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 relative" data-id="element-63">
        <div className="relative mb-16" data-id="element-64">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-65">
            03
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-66">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 flex items-center gap-5 text-slate-900 tracking-tight" data-id="element-67">
              <span className="w-10 h-1.5 bg-[#00BFA6] block rounded-full" data-id="element-68"></span>
              Infrastructure Map
            </h2>
            <p className="text-2xl text-slate-500 ml-15 pl-15" data-id="element-69">
              Layer-based architecture for complex energy data
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col md:flex-row gap-10 mb-16" data-id="element-70">
          <motion.div variants={childVariants} className="flex-1 relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white group" data-id="element-71">
            <img src="https://cdn.magicpatterns.com/uploads/vnwAG5X8RpWj1pTXGCda7t/Global_-_Infrastructure_Pipelines_1.3.png" alt="Pipeline Detail View" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-72" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none" data-id="element-73"></div>
          </motion.div>
          <motion.div variants={childVariants} className="flex-1 relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white group" data-id="element-74">
            <img src="https://cdn.magicpatterns.com/uploads/k19cNMWMgUsT1LRNviPd21/Global_-_Infrastructure_Regas_1.1.png" alt="Regas Terminal Detail" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-75" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none" data-id="element-76"></div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-77">
          {[{
          title: 'Progressive Disclosure',
          desc: 'Users start with a clean view and reveal layers as they need them. No information overload upfront.'
        }, {
          title: 'Data Detail Panels',
          desc: 'Click any element to get rich contextual info: route, capacity, status, and confidence level.'
        }, {
          title: 'Dual User Types',
          desc: 'Designed for analysts who want data density and consultants who need the narrative.'
        }].map((card, i) => <motion.div key={i} variants={childVariants} className="bg-white border border-slate-200 shadow-md p-8 rounded-2xl relative overflow-hidden" data-id="element-78">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-79"></div>
              <h4 className="text-[#00BFA6] font-bold text-xl mb-3" data-id="element-80">
                {card.title}
              </h4>
              <p className="text-base text-slate-600 leading-relaxed" data-id="element-81">
                {card.desc}
              </p>
            </motion.div>)}
        </div>
      </motion.section>

      <Divider data-id="element-82" />

      {/* SECTION 5 — ITERATIONS */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 relative" data-id="element-83">
        <div className="relative mb-16" data-id="element-84">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-85">
            04
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-86">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 flex items-center gap-5 text-slate-900 tracking-tight" data-id="element-87">
              <span className="w-10 h-1.5 bg-[#00BFA6] block rounded-full" data-id="element-88"></span>
              Prototyping and Iteration
            </h2>
            <p className="text-2xl text-slate-500 ml-15 pl-15 mb-8" data-id="element-89">
              From early concepts to production-ready components
            </p>
            <p className="text-slate-600 text-xl max-w-4xl ml-15 pl-15 leading-relaxed" data-id="element-90">
              I prototyped with both Mapbox and MapTiler, iterated on the legend
              and filter components in Figma, and tested multiple map styles to
              find the right balance between readability and data clarity.
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col gap-12" data-id="element-91">
          <motion.div variants={childVariants} className="flex flex-col md:flex-row gap-10 items-center bg-white p-10 rounded-3xl border border-slate-200 shadow-md relative overflow-hidden" data-id="element-92">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-93"></div>
            <div className="w-full md:w-1/3 space-y-4" data-id="element-94">
              <div className="inline-block px-4 py-1.5 bg-emerald-50 text-[#00BFA6] text-xs font-semibold rounded-full mb-2 border border-emerald-100/50" data-id="element-95">
                Figma
              </div>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-96">
                Legend and filter panel evolution
              </h3>
              <p className="text-slate-500 font-medium text-lg" data-id="element-97">
                prototype, current, proposed
              </p>
            </div>
            <div className="w-full md:w-2/3 relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-inner" data-id="element-98">
              <img src="https://cdn.magicpatterns.com/uploads/t51ErUsqGVW2DNiTqEd8TN/Global_-_Legend.png" alt="Figma Iterations" className="w-full h-auto object-cover object-left-top" data-id="element-99" />
            </div>
          </motion.div>

          <motion.div variants={childVariants} className="flex flex-col md:flex-row-reverse gap-10 items-center bg-white p-10 rounded-3xl border border-slate-200 shadow-md relative overflow-hidden" data-id="element-100">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[#00BFA6]/40 to-transparent" data-id="element-101"></div>
            <div className="w-full md:w-1/3 space-y-4" data-id="element-102">
              <div className="inline-block px-4 py-1.5 bg-emerald-50 text-[#00BFA6] text-xs font-semibold rounded-full mb-2 border border-emerald-100/50" data-id="element-103">
                Mapbox
              </div>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-104">
                Map style comparisons
              </h3>
              <p className="text-slate-500 font-medium text-lg" data-id="element-105">
                Testing satellite-streets-v12 across interaction states
              </p>
            </div>
            <div className="w-full md:w-2/3 relative rounded-2xl overflow-hidden border border-slate-200 bg-[#0D1117] shadow-inner" data-id="element-106">
              <img src="https://cdn.magicpatterns.com/uploads/6qbK4eK3D2R5G4WSevFyVN/Screenshot_2026-03-23_at_19.04.53.png" alt="Mapbox Iterations" className="w-full h-auto object-contain" data-id="element-107" />
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* SECTION 6 — CONSTRAINTS */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-5xl mx-auto px-8 relative" data-id="element-108">
        <div className="bg-white rounded-3xl shadow-md border border-slate-200 p-10 md:p-16 relative overflow-hidden" data-id="element-109">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-slate-200 via-[#00BFA6]/60 to-slate-200" data-id="element-110"></div>

          <div className="relative mb-16" data-id="element-111">
            <span className="absolute -top-16 -left-4 text-[120px] font-bold text-slate-50 leading-none z-0 select-none tracking-tighter" data-id="element-112">
              05
            </span>
            <motion.div variants={childVariants} className="relative z-10" data-id="element-113">
              <span className="inline-block bg-emerald-50 text-[#00BFA6] px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm border border-emerald-100/50" data-id="element-114">
                CONSTRAINTS
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight" data-id="element-115">
                What made this hard
              </h2>
            </motion.div>
          </div>

          <div className="flex flex-col" data-id="element-116">
            {[{
            num: '1',
            title: 'The scope kept shifting',
            desc: 'Both organisations were still figuring out what the product needed to be. Requirements changed regularly. I dealt with this by designing modular components that could be reconfigured without starting from scratch, and kept stakeholders aligned through visual artefacts rather than status reports.'
          }, {
            num: '2',
            title: 'Tooling changed mid-project',
            desc: "The map library switched from Mapbox to MapTiler partway through, and the design system approach shifted too. I treated these as design decisions rather than blockers, and adapted the prototypes quickly so we didn't lose momentum."
          }, {
            num: '3',
            title: 'Limited engineering bandwidth',
            desc: 'Dev resource was tight the whole time. So I prioritised ruthlessly, focused on the features that delivered the most user value per engineering hour, and made sure my designs were documented and build-ready to cut down on back-and-forth.'
          }, {
            num: '4',
            title: 'Tight deadline, late start',
            desc: 'I joined late with a fixed launch date. So I quickly mapped the current product state, identified the biggest UX gaps, and worked in parallel streams to deliver within the timeline.'
          }].map((challenge, i, arr) => <motion.div key={i} variants={childVariants} className={`flex gap-8 py-10 ${i !== arr.length - 1 ? 'border-b border-slate-100' : 'pb-0'}`} data-id="element-117">
                <div className="shrink-0" data-id="element-118">
                  <div className="w-14 h-14 rounded-full border-[3px] border-[#00BFA6] bg-emerald-50 text-[#00BFA6] flex items-center justify-center text-xl font-bold shadow-sm relative" data-id="element-119">
                    <div className="absolute inset-0 rounded-full bg-white/50" data-id="element-120"></div>
                    <span className="relative z-10" data-id="element-121">{challenge.num}</span>
                  </div>
                </div>
                <div className="flex-1 pt-1" data-id="element-122">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-123">
                    {challenge.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-lg" data-id="element-124">
                    {challenge.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </motion.section>

      <Divider data-id="element-125" />

      {/* SECTION 7 — COMMUNICATION */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 relative" data-id="element-126">
        <div className="relative mb-20" data-id="element-127">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-128">
            06
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-129">
            <span className="inline-block bg-emerald-50 text-[#00BFA6] px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm border border-emerald-100/50" data-id="element-130">
              WORKING WITH THE TEAM
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight" data-id="element-131">
              How I communicated and collaborated
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-132">
          {[{
          label: 'WITH PRODUCT',
          desc: 'Regular syncs to align on priority trade-offs. I used annotated Figma frames as the shared source of truth, which kept conversations focused on decisions rather than status updates.'
        }, {
          label: 'WITH ENGINEERING',
          desc: "I embedded myself in dev workflows. Provided detailed component specs, edge case documentation and interactive prototypes. Made myself available for async questions so developers weren't blocked waiting on design."
        }, {
          label: 'WITH STAKEHOLDERS',
          desc: 'Turned complex data requirements into visual prototypes that stakeholders could actually react to. Used map layer demos to get faster sign-off on interaction decisions that would have been hard to explain in words alone.'
        }].map((col, i) => <motion.div key={i} variants={childVariants} className="bg-white border border-slate-200 border-t-[3px] border-t-[#00BFA6] shadow-lg rounded-b-2xl p-10 flex flex-col relative overflow-hidden" data-id="element-133">
              <span className="uppercase text-xs tracking-widest text-slate-400 font-bold mb-6 block" data-id="element-134">
                {col.label}
              </span>
              <p className="text-slate-600 leading-relaxed text-lg" data-id="element-135">
                {col.desc}
              </p>
            </motion.div>)}
        </div>

        <motion.div variants={childVariants} className="bg-emerald-50/50 border border-emerald-100 border-l-4 border-l-[#00BFA6] rounded-r-3xl p-10 shadow-sm" data-id="element-136">
          <p className="italic text-slate-800 text-2xl font-medium leading-relaxed" data-id="element-137">
            The biggest thing I learned here: prototypes do the communicating
            for you. Instead of asking stakeholders to imagine how something
            would work, I just showed them. That sped up decisions and gave
            everyone a shared language.
          </p>
        </motion.div>
      </motion.section>

      {/* SECTION 8 — OUTCOME */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.15
    }} variants={sectionVariants} className="py-24 md:py-32 max-w-6xl mx-auto px-8 mb-24 relative" data-id="element-138">
        <div className="relative mb-16" data-id="element-139">
          <span className="absolute -top-12 -left-6 md:-left-12 text-[120px] font-bold text-slate-100 leading-none z-0 select-none tracking-tighter" data-id="element-140">
            07
          </span>
          <motion.div variants={childVariants} className="relative z-10" data-id="element-141">
            <span className="inline-block bg-emerald-50 text-[#00BFA6] px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm border border-emerald-100/50" data-id="element-142">
              OUTCOME AND VALUE
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-143">
              The tangible difference I made
            </h2>
            <p className="text-2xl text-slate-500 max-w-3xl leading-relaxed" data-id="element-144">
              This wasn't just about design output. I cleared the path to launch
              and set the team up for what comes next.
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col md:flex-row gap-16" data-id="element-145">
          {/* Left Column: Timeline */}
          <div className="flex-1 relative" data-id="element-146">
            <div className="absolute left-[15px] top-6 bottom-10 w-1 bg-[#00BFA6]/20 z-0 rounded-full" data-id="element-147"></div>

            <div className="flex flex-col gap-14 relative z-10" data-id="element-148">
              {[{
              title: 'Removed design blockers holding up engineering',
              desc: 'Found where missing or unclear design decisions were stalling development. Provided specs, edge-case documentation and interactive prototypes so engineers could build without waiting on me. Turned design from a bottleneck into an accelerator.'
            }, {
              title: 'Got the product to MVP-ready',
              desc: 'Joined late, quickly mapped the current state, prioritised the highest-impact UX gaps, and delivered a cohesive experience against a fixed launch date. Across the infrastructure map, data layers, and general platform UX.'
            }, {
              title: 'Shipped the infrastructure map with live layer controls',
              desc: 'The core deliverable. Users can toggle crude, gas, LNG, and green energy layers dynamically, exploring the full energy value chain in one interactive view.'
            }, {
              title: 'Wrote a fast-follow plan for post-launch',
              desc: "Documented a prioritised roadmap of UX improvements, unresolved edge cases, and design debt to tackle after launch. Gave the team a clear, actionable path forward so they didn't lose momentum after I rolled off."
            }].map((outcome, i) => <motion.div key={i} variants={childVariants} className="flex gap-10" data-id="element-149">
                  <div className="shrink-0 mt-1" data-id="element-150">
                    <motion.div animate={i === 0 ? {
                  scale: [1, 1.1, 1],
                  opacity: [1, 0.8, 1]
                } : {}} transition={i === 0 ? {
                  repeat: Infinity,
                  duration: 2,
                  ease: 'easeInOut'
                } : {}} className="w-8 h-8 rounded-full bg-[#00BFA6] shadow-md border-4 border-white ring-2 ring-emerald-100 relative z-10" data-id="element-151"></motion.div>
                  </div>
                  <div data-id="element-152">
                    <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-153">
                      {outcome.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-lg" data-id="element-154">
                      {outcome.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Right Column: Callout Card */}
          <motion.div variants={childVariants} className="flex-1" data-id="element-155">
            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200/60 border-b-4 border-b-[#00BFA6] rounded-3xl p-10 md:p-14 h-full flex flex-col justify-center shadow-lg relative overflow-hidden" data-id="element-156">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-100/30 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-157"></div>

              <h3 className="text-3xl font-bold text-[#00BFA6] mb-10 relative z-10" data-id="element-158">
                Not just design. Delivery.
              </h3>
              <p className="text-slate-800 text-xl leading-relaxed mb-8 font-medium relative z-10" data-id="element-159">
                My value on this project wasn't the design output alone. It was
                making sure nothing on the design side blocked the team from
                shipping.
              </p>
              <p className="text-slate-800 text-xl leading-relaxed mb-10 relative z-10" data-id="element-160">
                I came in, assessed what was missing, cleared the blockers, got
                us to a launch-ready product, and then made sure the team had a
                plan for what to improve next.
              </p>
              <div className="h-px w-16 bg-emerald-200 mb-8 relative z-10" data-id="element-161"></div>
              <p className="text-slate-600 text-lg leading-relaxed italic relative z-10" data-id="element-162">
                Constraints forced prioritisation. Prioritisation forced
                clarity. And clarity is what good product UX is built on.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </main>;
}